import Immutable, { Record, Map } from "immutable";
import me from "models/me";
import moment from "moment";
import { getStore } from "store";
import _ from "lodash";
import { TYPE_TECH } from "constants/pdf-types";
import { greatOrEqual, IBrandSubscription } from "constants/brand-subscriptions";
import type { IScope } from "reducers/scope";
import { SchemaRecord } from "records/schema";
import type { BrandUser } from "../../../typings/nuorder/brand/user";

export interface LegalFile {
    _id: string;
    name: string;
    label: string; // Order Confirmation Terms
    upload_id: string;
    created_on: Date;
    deleted_on?: Date;
    companies?: number;
}

export interface UpdateLegalFile {
    _id: string;
    label: string;
}

export interface ReplaceLegalFile {
    _id: string;
    // replacement data
    label: string;
    name: string;
    upload_id: string;
}

export interface IProductCustomizationSettings {
    prints: IProductCustomizationPrint[];
    image_types: IProductCustomizationImageType[];
}

export type TImageTypeMap = Map<string, IProductCustomizationImageType>;
export type TTypeDataMap = Map<string, CustomizationSettingsDataType>;

export type CustomizationSettingsDataType = IProductCustomizationPrint | IProductCustomizationImageType;

export interface IProductCustomizationPrint {
    _id: string;
    name: string;
    is_archived?: boolean;
}

export interface IProductCustomizationImageType {
    _id: string;
    name: string;
    accept_type: string;
    is_archived?: boolean;
}

export interface IBrandSettings {
    product_customizations?: IProductCustomizationSettings;
    order_split_fields?: string[];
    [key: string]: any;
}

const baseSwatchUrl = "//cdn1.nuorder.com/brand_color_swatch";

type TDefaults = {
    settings: IBrandSettings;
    [key: string]: any;
};

type IContactInformation = {
    address: {
        city?: string;
        state?: string;
        zip?: string;
        country?: string;
        line1?: string;
        line2?: string;
    };
    website?: string;
    phone?: string;
    emails?: string[];
    [key: string]: any;
};

const defaults: TDefaults = {
    _id: undefined,
    merchant_id: undefined,
    type: "",
    has_profile: undefined,
    can_export_to_ftp: false,
    active: false,
    name: "",
    site_version: 3,
    disabled: false,
    created_on: undefined,
    modified_on: undefined,
    preferences: Map,
    color_swatches: Map,
    picture: undefined,

    available_modules: [],
    galleries: [],
    connectedCompanies: [],
    schemas: [],
    views: [],

    modules: {},
    settings: {
        product_customizations: {
            prints: [],
            image_types: [],
        },
    },
    default_permissions: {},
    contact_information: {},
    __virtualConnection: false,
};

class BrandRecord extends Record(defaults) {
    declare name: string;
    declare swatch_type: string;
    declare galleries: any[];
    declare connectedCompanies: any[];
    declare contact_information: IContactInformation;
    declare modules: any;
    declare settings: IBrandSettings;
    declare created_on: any;
    declare active: boolean;
    declare preferences: Map<any, any>;
    declare available_modules: any;
    declare _id: any;
    declare merchant_id: string;
    declare value: any;
    declare default_permissions: any;
    declare _recentlyViewedItems: any;
    declare __virtualConnection: boolean;

    constructor(args: any = {}) {
        const { settings = {} } = args;

        super(
            Object.assign({}, args, {
                preferences: Map(settings?.preferences?.map((val) => [val.preference, val.value])),
                color_swatches: Map(args.modules?.base?.color_swatches?.map((val) => [val.color, val]) ?? []),
                order_quantity_rules: settings?.order_quantity_rules
                    ? Immutable.List(settings?.order_quantity_rules)
                    : Immutable.List(),
                guest_buyer_product_whitelist_values: settings?.guest_buyer_product_whitelist_values
                    ? Immutable.List(settings?.guest_buyer_product_whitelist_values)
                    : Immutable.List(),
            }),
        );
    }

    get _initials() {
        let initals = "";
        if (this.name) {
            const chunks = this.name.split(" ");
            for (let i = 0; i < chunks.length; i++) {
                if (i >= 2) {
                    break;
                }
                initals = initals + (chunks[i][0] || "");
            }
        }

        return initals.toUpperCase();
    }

    get _hasActiveGalleries() {
        const activeGalleries = this.galleries.filter((element) => {
            return element.active;
        });
        return !!activeGalleries.length;
    }

    get _users(): Partial<BrandUser>[] {
        return this.modules.base.user_connections || [];
    }

    get _brandConnection(): any {
        let brandUser = {};
        if (me.account_type === "brand") {
            brandUser = this.modules.base.user_connections.find((user) => {
                return user.ref === me._id;
            });
        } else if (me.account_type === "nuorder") {
            return {
                //stub for nuorder users
                //TODO remove after SAKS demo
                currency_code: "USD", // this.settings.default_currency || this.settings.currencies[0],
            };
        }

        return brandUser;
    }

    get _traits() {
        const traits = {
            Status: this.active ? "Paying" : "Cancelled",
            "Create Date": moment.utc(this.created_on).format("YYYY-MM-DD"),
            Licenses: this.modules.base.seats,
            Seats: this.modules.base.seats,
            name: this.name,
            "Default Currency": this.settings.default_currency,
            Currencies: (this.settings.currencies || []).join(","),
            "Enabled Modules": this.available_modules.join(","),
            "Subteam Count": (this.settings.subteams || []).length,
            Subscription: this.settings.subscription_type,
        };

        this.settings.preferences.forEach(function (p) {
            traits["zpref_" + p.preference] = p.value;
        });
        return traits;
    }

    hasColorSwatch() {
        return !!this.modules.base.color_swatches.length;
    }

    getSafeV3URI(pathname) {
        const fragments = pathname.split("/").filter((fragment) => fragment !== "");

        if (fragments.length !== 3 || !["admin", "management", "reporting", "orders"].includes(fragments[2])) {
            pathname = `/brand/${this._id}`;
        }
        return pathname;
    }

    getRulesForItem(item) {
        if (!this.hasPreference("auto_add_multiple_sizes")) {
            return [];
        }
        return _.filter(
            this.settings.order_quantity_rules,
            ({ rule_value, rule_field, rule_quantity }) =>
                rule_quantity && (!rule_field || (item[rule_field] && rule_value.includes(item[rule_field]))),
        );
    }

    getImageSrc() {
        if (!this.hasColorSwatch()) {
            return null;
        }

        return this.swatch_type !== "file" ? "" : `${baseSwatchUrl}/${this.value}.jpg`;
    }

    hasPreferences(preferences: string[]): boolean {
        return preferences.every((preference) => this.hasPreference(preference));
    }

    hasPreference(preference: string) {
        return this.getPreference(preference) || false;
    }

    getPreference(preference: string) {
        return this.preferences.get(preference);
    }

    buyersCanOrder() {
        const buyerPermissions = this.default_permissions.buyer;
        return buyerPermissions && buyerPermissions.indexOf("can_order") !== -1;
    }

    findBuyerConnection(user) {
        const companyConnection = me.store.getState().scope.company;

        if (companyConnection) {
            return (companyConnection.__buyers || []).find((buyer) => buyer.__ref === user._id);
        } else {
            return { role: "buyer" };
        }
    }

    getBuyerRoles() {
        const usingCustomRoles = this.hasPreference("allow_custom_roles");

        if (usingCustomRoles) {
            return this.settings.custom_roles
                .filter((role) => role.type === "buyer" && role.name !== "guest_buyer")
                .map((roleObj) => {
                    return roleObj.name;
                });
        } else {
            return ["buyer"];
        }
    }

    userHasPermission(user, permission, scope) {
        let userConnection: any = { role: "buyer" };
        const usingCustomRoles = this.hasPreference("allow_custom_roles");

        if (user.account_type === "nuorder") {
            return true;
        } else if (user.account_type === "brand") {
            userConnection = this.findUserConnection(user);
            if (!userConnection) {
                return false;
            }

            // See if they have an override
            if (userConnection.permissions.length) {
                return userConnection.permissions.indexOf(permission) !== -1;
            }
        } else if (user.account_type === "buyer") {
            if (usingCustomRoles && me.store) {
                userConnection = this.findBuyerConnection(user);
            }
            const globalPermissionsForBuyers = ["can_create_whiteboards"];
            if (globalPermissionsForBuyers.includes(permission)) {
                return true;
                // some permissions, like whiteboards are always available to buyers, even if brand
                // has not opted in to that feature
            }
        }

        let permissionsForRole = [];
        if (usingCustomRoles && userConnection && userConnection.role_name) {
            permissionsForRole = this.default_permissions[userConnection.role_name] || [];
        } else if (userConnection) {
            permissionsForRole = this.default_permissions[userConnection.role || "buyer"] || [];
        }

        const brandAllowsGuestBuyer = this.hasPreference("allow_guest_buyers");
        const isGuestBuyer = userConnection.role === "buyer" && (!scope.company || !scope.company._id);

        if (brandAllowsGuestBuyer && isGuestBuyer) {
            //have buyers use the guest buyer permission set when viewing a brand they are not connected to
            permissionsForRole = this.default_permissions["guest buyer"] || [];
        }

        return permissionsForRole.includes(permission);
    }

    findUserConnection(user) {
        const userId = user._id || user;
        let foundUser = this.modules.base.user_connections.find((user) => user.ref === userId);

        // weird issue I can't seem to track down, whereby somehow the brand.user_connection.x.ref
        // is overwritten to be the _id instead. In this case, email will still be unique, so not
        // the end of the world
        if (!foundUser && user.email && user.account_type === "brand") {
            foundUser = this.modules.base.user_connections.find((u) => u.email === user.email);
        }

        return foundUser;
    }

    searchBrandUsers(string) {
        return this.modules.base.user_connections.filter((brandUser) => {
            const pattern = new RegExp(string, "i");
            return brandUser.email.match(pattern) || brandUser.name.match(pattern);
        });
    }

    isGuestBuyer(user, scope) {
        return user.account_type === "buyer" && (!scope.company || !scope.company._id);
    }

    trackGroup() {
        if (me.isSignedIn()) {
            const groupId = this._id;
            const { _traits } = this;
            // @ts-ignore
            _traits.product_id = me.account_type;
            // @ts-ignore
            _traits.parent_id = `${this._id}_s`;
            (global as any).analytics.group(groupId, _traits);
        }
    }

    trackAction(action, data = {}) {
        if (me.isSignedIn()) {
            const mutatedData: any = Object.assign({}, data);
            mutatedData.category = mutatedData.category || "Wholesale";

            const groupId = this._id;
            mutatedData.GroupID = groupId;
            (global as any).analytics.track(action, mutatedData, { groupId });
        }
    }

    pushItemViewed(item) {
        this._recentlyViewedItems = this._recentlyViewedItems || Immutable.List();
        this._recentlyViewedItems = this._recentlyViewedItems.filter((recentItem) => {
            return recentItem !== item;
        });
        this._recentlyViewedItems = this._recentlyViewedItems.unshift(item);
        if (this._recentlyViewedItems.size > 10) {
            this._recentlyViewedItems = this._recentlyViewedItems.pop();
        }
        return this._recentlyViewedItems;
    }

    getRecentlyViewedItems() {
        this._recentlyViewedItems = this._recentlyViewedItems || Immutable.List();
        return this._recentlyViewedItems;
    }

    canCreateBrandProfile() {
        return this.hasPreference("allow_guest_buyers") && this.hasPreference("hide_from_buyers") === false;
    }

    hasModuleEnabled(module) {
        if (this.modules[module]) {
            return this.modules[module].enabled;
        } else {
            return false;
        }
    }

    hasValidWarehousesForShippingModule() {
        // shipping requires a valid postal_code and valid country_code (default us)
        const hasInvalidData = this.settings.warehouses.some((warehouse) => {
            return !warehouse.postal_code || !warehouse.country_code;
        });

        return !hasInvalidData;
    }

    hasBrandConfiguredForShipping() {
        const hasShippingModule = this.hasModuleEnabled("shipping");
        const hasWarehousesConfigured = this.hasValidWarehousesForShippingModule();
        let hasNecessaryWarehouseSplit = true;
        if (this.settings.warehouses.length > 1) {
            hasNecessaryWarehouseSplit = this.hasOrderSplit("warehouse");
        }

        return hasShippingModule && hasWarehousesConfigured && hasNecessaryWarehouseSplit;
    }

    hasOrderSplit(field) {
        return (
            this.settings.order_split_fields &&
            this.settings.order_split_fields.length > 0 &&
            this.settings.order_split_fields.includes(field)
        );
    }

    getSchema(type: "brand" | "company" | "order" | string): SchemaRecord | undefined {
        const store = getStore();
        const { schemas, entities } = store.getState();
        let schema;
        schemas.result.some((id) => {
            const schemaEntity = entities.schemas[id];
            if (schemaEntity.get("type") === type) {
                schema = schemaEntity;
                return true;
            } else {
                return false;
            }
        });

        return schema;
    }

    getPdfTemplates() {
        return _.filter(this.modules.base.templates, (template) => !template.draft && template.template !== TYPE_TECH);
    }

    /**
     * Check if brand has a subscription type.
     * @param {IBrandSubscription} subscription - subscription type to check for.
     * @return {boolean} has some subscription type.
     */
    checkSubscription(subscription: IBrandSubscription) {
        return greatOrEqual(this.settings.subscription_type, subscription);
    }

    isExternalPricingEnabled(): boolean {
        const allowed = ["external_call_volume_pricing", "external_call_volume_pricing_all"];
        return allowed.includes(this.settings.volume_pricing_customization_scripts);
    }

    /**
     * Check if brand has a pricing/promotions feature enabled.
     * @param {IScope} scope - nuorder scope.
     * @return {boolean} has some subscription type.
     */
    isPricingPromotionEnabled(scope: IScope): boolean {
        return (
            scope.brand.getPreference("enable_advanced_pricing") &&
            !_.isEmpty(scope.brand.getPreference("use_advance_pricing_for_all_prices")) &&
            this.settings.volume_pricing_customization_scripts === "pricing_service_volume_pricing"
        );
    }

    /**
     * Check if brand has advanced_pricing enabled (full advanced pricing case)
     * @param {IScope} scope - nuorder scope.
     * @return {boolean} has some subscription type.
     */
    isAdvancedPricingEnabled(scope: IScope): boolean {
        return (
            scope.brand.getPreference("enable_advanced_pricing") &&
            scope.brand.getPreference("use_advance_pricing_for_all_prices") === "advanced_pricing" &&
            this.settings.volume_pricing_customization_scripts === "pricing_service_volume_pricing"
        );
    }

    /**
     * Check if brand has a strikethrough enabled and pricing/promotions feature enabled.
     * @param {IScope} scope - nuorder scope.
     * @return {boolean} has some subscription type.
     */
    isStrikethroughEnabled(scope: IScope): boolean {
        const _isStrikethroughEnabled = _.get(this.settings, ["strikethrough", "is_enabled"], false);
        return this.isPricingPromotionEnabled(scope) && _isStrikethroughEnabled;
    }

    /** Returns selected currency code. Only for advanced pricing */
    getAdvancedPricingCurrencyCode(scope: IScope): string | undefined {
        return this.isPricingPromotionEnabled(scope) ? scope?.currencyCode || undefined : undefined;
    }

    /**
     * Checks if user is able to see list of products
     * @param {any} user - user details
     */
    canDisplayProductsToUser(user: any): boolean {
        return (
            (user.account_type !== "brand" || !this.hasPreference("hide_products_tab_for_brand")) &&
            (user.account_type !== "buyer" || !this.hasPreference("hide_products_tab_for_buyer"))
        );
    }
}

export { BrandRecord };
